import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { FaMicrophone } from "react-icons/fa";

import Loader from "../Common/Loader";
import {
  createHowToTakeAction,
  updateHowToTakeAction,
} from "../../../store/actions/HowToTakeAction";

const AddHowToTake = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { item } = location.state || {};
  const isEditMode = !!item;

  const [howToTakeText, setHowToTakeText] = useState("");
  const [status, setStatus] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // 🎤 Speech
  const recognitionRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [speechLang] = useState("en-IN");

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];
  /* ============================
     PREFILL DATA (EDIT MODE)
  ============================ */
  useEffect(() => {
    if (item) {
      setHowToTakeText(item.how_to_take_text || "");
      setStatus(item.how_to_take_status === "Active" ? 1 : 0);
    }
  }, [item]);

  /* ============================
     SPEECH TO TEXT
  ============================ */
  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = speechLang;
    recognition.interimResults = false;

    setIsRecording(true);

    recognition.onresult = (e) => {
      const spoken = e.results[0][0].transcript;
      setHowToTakeText((prev) => {
        const nextValue = prev ? prev + " " + spoken : spoken;
        clearFieldError("howToTakeText");
        return nextValue;
      });
    };

    recognition.onend = () => setIsRecording(false);
    recognition.start();
  };

  /* ============================
     VALIDATION
  ============================ */
  const validateForm = () => {
    const err = {};
    if (!howToTakeText.trim()) {
      err.howToTakeText = "How to take is required";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = (field, value) => {
    if (field === "howToTakeText") {
      setHowToTakeText(value);
    } else if (field === "status") {
      setStatus(value);
    }
    clearFieldError(field);
  };

  /* ============================
     SUBMIT
  ============================ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      how_to_take_text: howToTakeText,
      how_to_take_status: status === 1 ? "Active" : "Inactive",
    };

    if (isEditMode) {
      payload.how_to_take_id = item.how_to_take_id;
    }

    try {
      setLoading(true);

      if (isEditMode) await dispatch(
        updateHowToTakeAction(payload, item.how_to_take_id)
      );
      else {
        await dispatch(createHowToTakeAction(payload));
      }

      navigate("/app-how-to-take");
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">

        <div className="d-flex justify-content-end mb-3">
          <Link to="/app-how-to-take">
            <Button variant="primary">Back</Button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>

              {/* TEXT */}
              <div className="mb-3 position-relative">
                <label className="form-label">
                  How To Take <span className="text-danger">*</span>
                </label>

                <textarea
                  className="form-control"
                  rows="5"
                  value={howToTakeText}
                  style={{ padding: "0.375rem 0.75rem", paddingTop: "0.6rem" }}
                  onChange={(e) =>
                    handleFieldChange("howToTakeText", e.target.value)
                  }
                />

                <button
                  type="button"
                  onClick={startListening}
                  disabled={isRecording}
                  style={{
                    position: "absolute",
                    right: 10,
                    bottom: 10,
                    borderRadius: "50%",
                    border: "none",
                    padding: 8,
                    background: isRecording ? "#ff4d4d" : "#eee",
                  }}
                >
                  <FaMicrophone />
                </button>

                {errors.howToTakeText && (
                  <div className="text-danger mt-1">
                    {errors.howToTakeText}
                  </div>
                )}
              </div>

              {/* STATUS */}
              <div className="mb-3">
                <label className="form-label">
                  Status <span className="text-danger">*</span>
                </label>

                <Select
                  options={statusOptions}
                  value={statusOptions.find(
                    (opt) => opt.value === status
                  )}
                  onChange={(selectedOption) =>
                    setStatus(selectedOption ? selectedOption.value : "")
                  }
                  placeholder="Select Status"
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      height: "50px",
                      borderColor: errors.status
                        ? "#dc3545"
                        : provided.borderColor,
                      boxShadow: errors.status
                        ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                        : provided.boxShadow,
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      lineHeight: "50px",
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: "4px",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                  }}
                />

                {errors.status && (
                  <small className="text-danger">{errors.status}</small>
                )}
              </div>


              {/* BUTTON */}
              <button className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size="sm" /> Saving...
                  </>
                ) : isEditMode ? (
                  "Update How To Take"
                ) : (
                  "Add How To Take"
                )}
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddHowToTake;
